import React, { FC, Fragment, useState } from "react";
import styled, { css } from "styled-components";
import { useMediaContext, mq, device } from "lib/media-query";
import { NavStyledArrow } from "components/Navigation/Elements/Desktop";
import DropdownIcon from "svg/arrow-down-nav.svg";
import BrandLogo from "svg/logo-autotrack-current.svg";
import { Screens } from "helpers/screens";
import { PRIVACY_POLICY_URL } from "consts";
import { FooterListHeader } from "./elements/FooterListHeader";
import { StyledResponsiveBlock } from "./elements/StyledResponsiveBlock";
import { FooterRow } from "./elements/FooterRow";
import { StyledLink } from "./elements/StyledLink";

const year = new Date().getFullYear();

const StyledFooter = styled.div`
    background-color: ${({ theme }) => theme.colors.tertiary[200]};
    color: ${({ theme }) => theme.colors.black};
    overflow: hidden;
    padding-top: 2rem;
`;
const StyledFooterBottom = styled(StyledFooter)`
    background-color: ${({ theme }) => theme.colors.tertiary[400]};
    padding: 20px 20px 10px 20px;
`;

const FooterSection = styled.div`
    padding: 0;
    margin: 0 20px 0;
`;

const FooterColumn = styled.div``;

const FooterList = styled.ul`
    padding-left: 0;
    list-style: none;
`;

const FooterListItem = styled.li`
    margin-bottom: 4px;

    ${mq.to(device.TABLET)`
    margin-bottom: 4px;
  `}

    a {
        color: ${({ theme }) => theme.colors.black};
        cursor: pointer;
        text-decoration: none;
    }

    &:active,
    :hover {
        a {
            color: ${({ theme }) => theme.colors.black};
            cursor: pointer;
            text-decoration: none;
        }
    }
`;

const CopyrightText = styled.div`
    font-size: 0.875rem;
    font-weight: 400;
`;

const MobileFooterMenu = [
    {
        menuTitle: "Auto Aanbod",
        dropdownItems: [
            { title: "Alle auto's", link: "/aanbod" },
            {
                title: "Hybride auto",
                link: "/aanbod/brandstofsoort/hybride-benzine"
            },
            {
                title: "Stationwagen",
                link: "/aanbod/carrosserievorm/stationwagen"
            },
            { title: "SUV", link: "/aanbod/carrosserievorm/suv" },
            {
                title: "Elektrische auto",
                link: "/aanbod/brandstofsoort/elektriciteit"
            }
        ]
    },
    {
        menuTitle: "Auto Informatie",
        dropdownItems: [
            { title: "Merken & Modellen", link: "/auto" },
            { title: "Blog", link: "/blog" },
            { title: "Autobedrijf Zoeken", link: "/autobedrijf/zoeken" },
            { title: "RDW Voertuigrapport", link: "/rdw-voertuigrapport" }
        ]
    },
    {
        menuTitle: "AutoTrack",
        dropdownItems: [
            { title: "Contact met AutoTrack", link: "/contact" },
            { title: "Over AutoTrack", link: "/over-autotrack" },
            {
                title: "Adverteren op AutoTrack",
                link: "https://www.autotrack.nl/adverteren"
            }
        ]
    },
    {
        menuTitle: "Advies",
        dropdownItems: [
            { title: "Aankoopadvies", link: "/blog/advies/aankoopadvies-aankoophulp" },
            {
                title: "Onderhandelen",
                link: "/blog/advies/aankoopadvies-onderhandelen"
            },
            {
                title: "Auto kiezen",
                link: "/kopen/auto-kiezen"
            }
        ]
    },
    {
        menuTitle: "Cookies & Privacy",
        dropdownItems: [
            { title: "Gebruiksvoorwaarden", link: "https://www.autotrack.nl/gebruiksvoorwaarden" },
            {
                title: "Privacy Informatie",
                link: "/algemeen/privacystatement"
            },
            { title: "Auteursrechten", link: "/auteursrechten" },
            {
                title: "Cookie Informatie",
                link: "/algemeen/cookieinfo"
            },
            {
                title: "Cookie Informatie",
                link: ""
            },
            {
                title: "Toegankelijkheidsverklaring",
                link: "/toegankelijkheidsverklaring"
            }
        ]
    }
] as const;

export const MobileDropdownMenuHeader = styled.div<{ active?: boolean }>`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    cursor: pointer;
    width: 100%;
    padding: 0.625rem;
    align-items: flex-start;
    gap: 1.25rem;
    align-self: stretch;
    border-radius: 0.25rem;
    color: ${({ theme }) => theme.colors.black};
    font-weight: 600;

    svg {
        fill: ${({ theme, active }) => (active ? theme.colors.white : theme.colors.secondary[700])};
    }

    :hover {
        svg {
            fill: ${({ theme }) => theme.colors.white};
        }
    }
`;

export const FooterMobileDropdownMenu = styled.ul<{ active?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: 0.625rem;
    align-self: stretch;
    font-size: 0.875rem;
    font-weight: 400;
    font-style: normal;
    line-height: 1rem;
    color: ${({ theme }) => theme.colors.black};
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s linear, visibility 0.3s linear, max-height 0.3s ease-out;
    margin-left: -2.5rem;

    ${({ active }) =>
        active &&
        css`
            opacity: 1;
            visibility: visible;
            max-height: 800px;
        `}
`;

export const FooterMobileDropdownItem = styled.li`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 0.625rem;
    gap: 0.625rem;
    align-self: stretch;

    a {
        color: ${({ theme }) => theme.colors.black};
        font-size: 0.875rem;
        font-weight: 400;
    }
`;

const DefaultTopSection = () => {
    const mediaContext = useMediaContext();
    const isMobilePhone = mediaContext.isMobile || mediaContext.isMobilePlus;
    const [isOpen, setIsOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [hamburgerMenuClicked, sethamburgerMenuClicked] = useState(false);

    const showDropdown = (selectedDropdown: string) => {
        setIsOpen(!isOpen);
        activeMenu !== selectedDropdown ? setActiveMenu(selectedDropdown) : setActiveMenu(null);
    };

    const toggleHamburgerMenu = () => {
        hamburgerMenuClicked ? sethamburgerMenuClicked(false) : sethamburgerMenuClicked(true);
    };

    if (isMobilePhone) {
        return (
            <section>
                {MobileFooterMenu.map((item) => {
                    return (
                        <>
                            <MobileDropdownMenuHeader
                                onClick={() => showDropdown(item.menuTitle)}
                                active={activeMenu === item.menuTitle}
                            >
                                {item.menuTitle}
                                <NavStyledArrow isOpen={activeMenu === item.menuTitle}>
                                    <DropdownIcon />
                                </NavStyledArrow>
                            </MobileDropdownMenuHeader>
                            <FooterMobileDropdownMenu active={activeMenu === item.menuTitle}>
                                {item.dropdownItems.map((footerItem) => {
                                    return (
                                        <Fragment key={footerItem.title}>
                                            <FooterMobileDropdownItem onClick={toggleHamburgerMenu}>
                                                <StyledLink href={footerItem.link} itemProp="name">
                                                    {footerItem.title}
                                                </StyledLink>
                                            </FooterMobileDropdownItem>
                                        </Fragment>
                                    );
                                })}
                            </FooterMobileDropdownMenu>
                        </>
                    );
                })}
            </section>
        );
    }

    return (
        <FooterSection>
            <FooterRow>
                <FooterColumn>
                    <FooterListHeader>Auto Aanbod</FooterListHeader>
                    <FooterList itemScope itemType="https://www.schema.org/SiteNavigationElement">
                        <FooterListItem>
                            <StyledLink href="/aanbod" itemProp="name">
                                Alle auto's
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink
                                href="/aanbod/brandstofsoort/hybride-benzine"
                                itemProp="name"
                            >
                                Hybride auto
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/aanbod/carrosserievorm/stationwagen" itemProp="name">
                                Stationwagen
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/aanbod/carrosserievorm/suv" itemProp="name">
                                SUV
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/aanbod/brandstofsoort/elektriciteit" itemProp="name">
                                Elektrische auto
                            </StyledLink>
                        </FooterListItem>
                    </FooterList>
                </FooterColumn>

                <FooterColumn>
                    <FooterListHeader>Auto Informatie</FooterListHeader>
                    <FooterList>
                        <FooterListItem>
                            <StyledLink href="/auto" itemProp="name">
                                Merken & Modellen
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/blog" itemProp="name">
                                Blog
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/autobedrijf/zoeken" itemProp="name">
                                Autobedrijf Zoeken
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/rdw-voertuigrapport" itemProp="name">
                                RDW Voertuigrapport
                            </StyledLink>
                        </FooterListItem>
                    </FooterList>
                </FooterColumn>

                <FooterColumn>
                    <FooterListHeader>AutoTrack</FooterListHeader>
                    <FooterList>
                        <FooterListItem>
                            <StyledLink href="/contact" itemProp="name">
                                Contact met AutoTrack
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/over-autotrack">Over AutoTrack</StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="https://www.autotrack.nl/adverteren" itemProp="name">
                                Adverteren op AutoTrack
                            </StyledLink>
                        </FooterListItem>
                    </FooterList>
                </FooterColumn>

                <FooterColumn>
                    <FooterListHeader>Advies</FooterListHeader>
                    <FooterList>
                        <FooterListItem>
                            <StyledLink
                                href="/blog/advies/aankoopadvies-aankoophulp"
                                itemProp="name"
                            >
                                Aankoopadvies
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink
                                href="/blog/advies/aankoopadvies-onderhandelen"
                                itemProp="name"
                            >
                                Onderhandelen
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/kopen/auto-kiezen" itemProp="name">
                                Auto kiezen
                            </StyledLink>
                        </FooterListItem>
                    </FooterList>
                </FooterColumn>

                <FooterColumn>
                    <FooterListHeader>Algemeen</FooterListHeader>
                    <FooterList>
                        <FooterListItem>
                            <StyledLink
                                href="https://www.autotrack.nl/gebruiksvoorwaarden"
                                itemProp="name"
                                target="_blank"
                                rel="noopener nofollow noreferrer"
                            >
                                Gebruiksvoorwaarden
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink
                                href="https://privacy.dpgmedia.nl/nl/document/terms-of-use#ip"
                                itemProp="name"
                                target="_blank"
                                rel="noopener nofollow noreferrer"
                            >
                                Auteursrecht
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink
                                href={PRIVACY_POLICY_URL}
                                target="_blank"
                                rel="noopener nofollow noreferrer"
                                itemProp="name"
                            >
                                Privacy statement
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink
                                href="https://www.autotrack.nl/cookie-policy"
                                target="_blank"
                                rel="noopener nofollow noreferrer"
                                itemProp="name"
                            >
                                Cookie statement
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink
                                onClick={() => {
                                    const returnUrl = window.location.href;
                                    window.open(
                                        `/consent-management?redirectTo=${encodeURIComponent(
                                            returnUrl
                                        )}`,
                                        "_blank",
                                        "noopener,noreferrer"
                                    );
                                }}
                                itemProp="name"
                            >
                                Privacy instellingen
                            </StyledLink>
                        </FooterListItem>
                        <FooterListItem>
                            <StyledLink href="/toegankelijkheidsverklaring" itemProp="name">
                                Toegankelijkheidsverklaring
                            </StyledLink>
                        </FooterListItem>
                    </FooterList>
                </FooterColumn>
            </FooterRow>
        </FooterSection>
    );
};

const CopywriteLogoWrapper = styled.div`
    display: grid;
    grid-template-rows: repeat(2, 1fr);
    gap: 1rem;

    svg {
        fill: ${({ theme }) => theme.colors.secondary[800]};
        grid-row: 1/2;
        align-self: center;
        justify-self: start;
    }

    @media (${Screens.get("lg")}) {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: unset;

        svg {
            grid-row: auto;
            justify-self: end;
        }
    }
`;

const copyrightFooter = (
    <StyledFooterBottom>
        <StyledResponsiveBlock>
            <CopywriteLogoWrapper>
                <CopyrightText>
                    © {year} AutoTrack - AutoTrack is onderdeel van AutoScout24 Nederland
                </CopyrightText>
                <BrandLogo />
            </CopywriteLogoWrapper>
        </StyledResponsiveBlock>
    </StyledFooterBottom>
);

interface FooterProps {
    middleSection?: JSX.Element;
}

export const Footer: FC<FooterProps> = ({ middleSection }) => {
    const mediaContext = useMediaContext();
    const { isMobile, isMobilePlus } = mediaContext;

    return (
        <>
            <StyledFooter className="cva pfoot">
                <StyledResponsiveBlock>
                    <DefaultTopSection />

                    {middleSection ? <FooterSection>{middleSection}</FooterSection> : ""}

                    {/* {!isMobile ? (
                    copyrightFooter
                ) : (
                    <FooterSection>
                        <CopyrightText>© {year} AutoTrack</CopyrightText>
                    </FooterSection>
                )} */}
                    {isMobile && isMobilePlus && (
                        <FooterSection>
                            <CopyrightText>© {year} AutoTrack</CopyrightText>
                        </FooterSection>
                    )}
                </StyledResponsiveBlock>
            </StyledFooter>
            {!isMobile && copyrightFooter}
        </>
    );
};
